import { FC, SyntheticEvent, useEffect, useRef } from 'react';
import styled from 'styled-components';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';

import 'video.js/dist/video-js.css';

import { media } from '../layout';

type VideoJSProps = {
  // there are no type definitions for options from video.js
  // https://github.com/videojs/video.js/issues/8646
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
  onReady?: (player: Player) => void;
  className?: string;
};

export const VideoPlayer: FC<VideoJSProps> = ({ options, className, onReady }) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);

  useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement('video-js');

      videoElement.classList.add('vjs-big-play-centered');
      videoElement.style.height = '100%';
      videoRef.current.appendChild(videoElement);

      const player = videojs(videoElement, options, () => {
        player.on('mouseup', (e: SyntheticEvent) => {
          if (!player.isReady_ || player.seeking()) return;
          if ((e.target as HTMLElement).closest('.vjs-control-bar')) {
            return;
          }
          if (player.paused() || player.ended()) {
            player.play();
            return;
          }
          player.pause();
        });
        player.on('touchstart', () => {
          if (!player.isReady_) return;
        });
        player.on('touchend', (e: SyntheticEvent) => {
          if (!player.isReady_) return;
          if ((e.target as HTMLElement).closest('.vjs-control-bar')) {
            return;
          }
          e.preventDefault();
          if (player.seeking()) return;
          if (player.paused()) {
            player.play();
            return;
          }
          player.pause();
        });
      });
      playerRef.current = player;
      onReady?.(player);
    } else {
      if (!playerRef.current) return;
      const player = playerRef.current;

      player.src(options.sources);
      player.autoplay(options.autoplay);
    }
  }, [options, videoRef]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <Container data-vjs-player>
      <VideoWrapper className={className} ref={videoRef} />
    </Container>
  );
};

export default VideoPlayer;

const Container = styled.div`
  height: 630px;

  ${media.mobile} {
    height: 100%;
  }
`;

const VideoWrapper = styled.div`
  height: 100%;
`;
